import React, { useState } from 'react';
import { ShieldCheckIcon } from '@heroicons/react/24/outline';
import { useAuth, MfaRequiredResult } from '@/hooks/useAuth';

interface MfaChallengeFormProps {
  challenge: MfaRequiredResult;
  onBack: () => void;
  onSuccess: () => void;
}

/**
 * The second step of login when the account has MFA enabled — shared by
 * LoginPage (password step) and RegisterPage (an existing MFA-enabled
 * account signing in via the "Sign up with Google" button, since Google
 * auth finds-or-creates rather than strictly creating). Both call the same
 * backend challenge-token flow, so this is one component rather than two
 * near-identical copies.
 */
const MfaChallengeForm: React.FC<MfaChallengeFormProps> = ({ challenge, onBack, onSuccess }) => {
  const { completeMfaLogin } = useAuth();
  const [code, setCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      await completeMfaLogin(challenge.challengeToken, code);
      onSuccess();
    } catch {
      // Error toast is handled by completeMfaLogin
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
      <div className="max-w-md w-full space-y-6 p-8">
        <div className="text-center">
          <ShieldCheckIcon className="w-10 h-10 text-blue-600 dark:text-blue-400 mx-auto mb-2" />
          <h2 className="text-3xl font-extrabold text-gray-900 dark:text-white">Verify it's you</h2>
          <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">{challenge.message}</p>
        </div>

        <form className="space-y-4" onSubmit={handleSubmit}>
          <input
            type="text"
            inputMode="numeric"
            autoFocus
            required
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder="6-digit code (or a backup code)"
            className="relative block w-full px-3 py-2 text-center text-lg tracking-widest border border-gray-300 dark:border-gray-600 placeholder-gray-500 dark:placeholder-gray-400 text-gray-900 dark:text-white bg-white dark:bg-gray-800 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
          />
          <button
            type="submit"
            disabled={isLoading || !code}
            className="w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
          >
            {isLoading ? 'Verifying…' : 'Verify'}
          </button>
          <button type="button" onClick={onBack} className="w-full text-center text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200">
            Back
          </button>
        </form>
      </div>
    </div>
  );
};

export default MfaChallengeForm;
